import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The `⋯` menu on a timesheet row. One item today — Duplicate — and the dropdown is
 * the place the later row actions (delete, split) land once the backend has them.
 *
 * Open/close follows the Now `MoveWorkMenu`: an outside mousedown closes it, and so
 * does Esc (which also returns focus to the trigger). The popup opens UPWARD because
 * the week panel clips its overflow, so a downward popup on the last row would be cut
 * off; every row has the day and week headers above it to open into.
 *
 * The caller owns the lock: pass `disabled` for as long as the row is busy (a duplicate or
 * a save in flight) and both the trigger and the item are disabled, so a second click
 * cannot log a second copy. While `onDuplicate` runs the item reads "Duplicating…", and
 * the menu closes once the call settles.
 *
 * @param {() => (Promise<unknown>|unknown)} onDuplicate Runs the duplicate; the menu waits on it.
 * @param {boolean} [duplicateDisabled] Disables the item (e.g. the entry has no description).
 * @param {string} [duplicateTitle] Tooltip for the item, used to say why it is disabled.
 * @param {boolean} [disabled] The row's lock: disables the `⋯` trigger and the item.
 * @param {() => void} [onChangeProject] Adds a "Change project" item that opens the move dialog.
 * @param {() => void} [onChangeDate] Adds "Change date".
 * @param {() => void} [onChangeTime] Adds "Change time".
 *   The three move items exist for the widths where the row hides the project label, the
 *   date control and the start time (below `lg`), and are hidden from `lg` up, where the
 *   row carries them itself. A caller that does not pass them gets today's menu.
 * @param {() => void} [onDelete] Adds a red "Delete" item below the rest. It only asks the
 *   row to open its confirmation; nothing is deleted by choosing it.
 */
export default function EntryMenu({
  onDuplicate, duplicateDisabled = false, duplicateTitle, disabled = false,
  onChangeProject, onChangeDate, onChangeTime, onDelete,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const duplicate = useCallback(async () => {
    setBusy(true);
    try {
      await onDuplicate?.();
    } finally {
      setBusy(false);
      setOpen(false);
    }
  }, [onDuplicate]);

  /** Closes the menu, then runs one of the move items' handlers. */
  const choose = (handler) => () => {
    setOpen(false);
    handler();
  };

  const moveItems = [
    { handler: onChangeProject, label: 'Change project', testId: 'row-menu-project' },
    { handler: onChangeDate, label: 'Change date', testId: 'row-menu-date' },
    { handler: onChangeTime, label: 'Change time', testId: 'row-menu-time' },
  ].filter((item) => item.handler);

  return (
    <div className="relative" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More actions"
        title="More actions"
        className="p-1 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:pointer-events-none"
        data-testid="row-menu"
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="1.75" />
          <circle cx="12" cy="12" r="1.75" />
          <circle cx="19" cy="12" r="1.75" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 bottom-full mb-1 z-20 w-40 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-1"
          data-testid="row-menu-popover"
        >
          <button
            type="button"
            role="menuitem"
            onClick={duplicate}
            disabled={duplicateDisabled || disabled}
            title={duplicateDisabled ? duplicateTitle : undefined}
            className="flex w-full items-center rounded-md px-2 py-1.5 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:pointer-events-none"
            data-testid="row-duplicate"
          >
            {busy ? 'Duplicating…' : 'Duplicate'}
          </button>
          {moveItems.map((item) => (
            <button
              key={item.testId}
              type="button"
              role="menuitem"
              onClick={choose(item.handler)}
              disabled={disabled}
              className="lg:hidden flex w-full items-center rounded-md px-2 py-1.5 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:pointer-events-none"
              data-testid={item.testId}
            >
              {item.label}
            </button>
          ))}
          {onDelete && (
            <button
              type="button"
              role="menuitem"
              onClick={choose(onDelete)}
              disabled={disabled}
              className="flex w-full items-center rounded-md px-2 py-1.5 text-sm text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:pointer-events-none"
              data-testid="row-delete"
            >
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}
